/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from '../i18n/useTranslation';
import { SavedOutfit, Lookbook, SavedContent, UserProfile, AppPage } from '../types';
import { ChevronLeftIcon, DownloadIcon } from '../components/icons';

interface StyleReportPageProps {
  userProfile: UserProfile;
  savedOutfits: SavedOutfit[];
  lookbooks: Lookbook[];
  savedContent: SavedContent[];
  currentUserId: string;
  onNavigate: (page: AppPage) => void;
}

const StyleReportPage: React.FC<StyleReportPageProps> = ({
  userProfile,
  savedOutfits,
  lookbooks,
  savedContent,
  currentUserId,
  onNavigate,
}) => {
  const { t } = useTranslation();

  const stylePlan = savedContent.find(c => c.type === 'plan');
  const username = currentUserId.split('-')[0].charAt(0).toUpperCase() + currentUserId.split('-')[0].slice(1);
  const today = new Date().toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const handlePrint = () => {
    window.print();
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <>
      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          html, body {
            background-color: #fff !important;
            color: #000 !important;
          }
          .printable-area {
            box-shadow: none !important;
            border: none !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          .page-break {
            page-break-before: always;
          }
          h1, h2, h3, p, span, div, li {
            color: #000 !important;
          }
          img {
            -webkit-print-color-adjust: exact;
            color-adjust: exact;
          }
        }
      `}</style>
      <div className="bg-gray-100 dark:bg-gray-900 min-h-full p-4 sm:p-8">
        {/* Action Bar */}
        <motion.div
          variants={itemVariants}
          initial="hidden"
          animate="visible"
          className="no-print max-w-5xl mx-auto mb-6 flex justify-between items-center"
        >
          <button
            onClick={() => onNavigate('my-styles')}
            className="flex items-center gap-2 text-gray-700 dark:text-gray-300 font-semibold hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
          >
            <ChevronLeftIcon className="w-5 h-5" />
            {t('styleReport_back')}
          </button>
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 bg-indigo-600 text-white font-semibold py-2 px-5 rounded-lg hover:bg-indigo-700"
          >
            <DownloadIcon className="w-5 h-5" />
            {t('styleReport_print')}
          </button>
        </motion.div>

        {/* Printable Content */}
        <motion.div
          variants={itemVariants}
          initial="hidden"
          animate="visible"
          className="printable-area max-w-5xl mx-auto bg-white p-8 sm:p-12 shadow-lg"
        >
          {/* Cover Page */}
          <section className="text-center h-[80vh] flex flex-col items-center justify-center border-b-2 border-gray-200 pb-8">
            <h1 className="text-5xl md:text-6xl font-serif font-bold text-gray-900">{t('styleReport_title')}</h1>
            <p className="mt-4 text-xl text-gray-600">{t('styleReport_generatedFor', { username })}</p>
            <p className="mt-2 text-md text-gray-500">{t('styleReport_date')}: {today}</p>
          </section>

          {/* Style Persona */}
          <section className="page-break pt-12">
            <h2 className="text-3xl font-serif font-semibold text-gray-800 border-b pb-2 mb-6">{t('styleReport_profile_title')}</h2>
            {userProfile.stylePersona ? (
              <div className="prose prose-lg max-w-none" dangerouslySetInnerHTML={{ __html: userProfile.stylePersona }} />
            ) : (
              <p className="text-gray-500">{t('styleReport_no_persona')}</p>
            )}
          </section>

          {/* Saved Outfits */}
          {savedOutfits.length > 0 && (
            <section className="page-break pt-12">
              <h2 className="text-3xl font-serif font-semibold text-gray-800 border-b pb-2 mb-6">{t('styleReport_outfits_title')}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                {savedOutfits.map(outfit => (
                  <div key={outfit.id} className="border border-gray-200 rounded-lg overflow-hidden break-inside-avoid">
                    <img src={outfit.previewImageUrl || ''} alt={outfit.name} className="w-full aspect-[3/4] object-cover bg-gray-100" />
                    <div className="p-4">
                      <h3 className="font-bold text-lg">{outfit.name}</h3>
                      <p className="text-sm text-gray-600 mt-1">{outfit.description}</p>
                      {outfit.tags && outfit.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {outfit.tags.map(tag => <span key={tag} className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded-full">#{tag}</span>)}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Lookbooks */}
          {lookbooks.length > 0 && (
            <section className="page-break pt-12">
              <h2 className="text-3xl font-serif font-semibold text-gray-800 border-b pb-2 mb-6">{t('styleReport_lookbooks_title')}</h2>
              <div className="space-y-8">
                {lookbooks.map(lb => lb.lookbookImageUrl && lb.type === 'collage' ? (
                  <div key={lb.id} className="break-inside-avoid">
                    <h3 className="font-bold text-xl mb-2">{lb.name}</h3>
                    <img src={lb.lookbookImageUrl} alt={lb.name} className="w-full object-contain rounded-lg border border-gray-200" />
                  </div>
                ) : null)}
              </div>
            </section>
          )}
          
          {/* Style Plan */}
          <section className="page-break pt-12">
            <h2 className="text-3xl font-serif font-semibold text-gray-800 border-b pb-2 mb-6">{t('styleReport_plans_title')}</h2>
            {stylePlan ? (
              <div>
                <h3 className="font-bold text-xl mb-2">{stylePlan.title}</h3>
                <div className="prose prose-lg max-w-none" dangerouslySetInnerHTML={{ __html: stylePlan.content }} />
              </div>
            ) : (
                <p className="text-gray-500">{t('styleReport_no_plans')}</p>
            )}
          </section>

        </motion.div>
      </div>
    </>
  );
};

export default StyleReportPage;
